<template>
  <div
    v-if="goal"
    class="row goal-container position-relative m-0"
    :class="{
      [`type-${type}`]: true,
      [viewType]: true,
      'select-list-item': selectable,
      'show-gripper': showGripper
    }"
    :data-goal-id="goal.id"
  >
    <div
      class="gripper-icon gripper"
      :class="getGripperClass"
      :style="getGripperStyle"
      v-if="showGripper && getGripperClass"
    ></div>
    <div
      class="goal-viewer-wrapper row mx-0 flex-grow-1"
      :class="{ [viewType]: true, expanded: expandAccordion }"
    >
      <div class="col goal-content-container p-0">
        <div
          class="top-bar variant"
          :data-variant="viewType === 'detail' ? colorVariant : ''"
          data-apply-light-bg-color
          @mouseenter="showOptions = true"
        >
          <parent-goals-bar :goal-id="goalId" v-if="showAlignment" />
          <div class="goal-title-container row justify-content-between flex-nowrap">
            <collapse-caret
              class="flex-shrink-0 collapse-caret"
              :size="23"
              @click="handleExpanderClick"
              :expanded="expandAccordion"
              v-if="canToggleExpansion"
            />
            <div class="d-flex justify-content-between align-items-center flex-grow-1 goal-content">
              <div class="col-auto icons-col d-flex align-items-center align-self-baseline">
                <div class="col-auto px-0 goal-icon-container d-flex">
                  <i class="goal-icon-element" :class="goalIcon" :id="'goal-icon-' + uuid"></i>
                  <tooltip :target="'goal-icon-' + uuid">{{ goalStatusLabel }}</tooltip>
                </div>
                <ownership-short
                  class="d-inline-flex"
                  :entity="goal"
                  entity-type="goal"
                  :length="viewType === 'detail' ? 40 : 30"
                ></ownership-short>
              </div>
              <div
                class="col goal-title-col"
                @click="handleTitleClick"
                :class="{ 'inline-edit': editable, 'position-relative': isSubmittingGoal }"
              >
                <input
                  type="text"
                  class="flex-grow-1 goal-title-input text-normal"
                  v-model="goalTitle"
                  @keyup="lastKey = $event.keyCode"
                  @keyup.enter="saveGoalTitle"
                  placeholder="Enter goal ([enter] to save, [esc] to cancel)"
                  v-autofocus="editable"
                  @blur="
                    () =>
                      lastKey !== KEY_CODES.ENTER && lastKey !== KEY_CODES.ESCAPE && saveGoalTitle()
                  "
                  @focus="editable = true"
                  data-prevent-blur="true"
                  @click.stop
                  v-if="editable"
                />
                <h2 class="goal-title d-flex col" v-else>
                  <span
                    class="goal-title-value wrap-hyphenate"
                    :class="{ bold: expandAccordion }"
                    :title="goal.title"
                    v-no-capture
                  >
                    {{ goal.title }}
                  </span>
                  <span class="icons-list align-self-baseline mx-2">
                    <span
                      :id="'parent-goal-' + uuid + '-' + goal.id"
                      class="parent-node-icon ml-2"
                      @click.stop
                      v-if="hasParentGoals && viewType === 'list'"
                      ><i></i
                    ></span>
                    <parent-goals-popover
                      v-if="hasParentGoals && viewType === 'list'"
                      :target="'parent-goal-' + uuid + '-' + goal.id"
                      :goal="goal"
                    />
                    <span
                      :data-comment-count="goal.commentCount"
                      class="comment-badge-icon pointer"
                      :id="'comment-badge-' + uuid + goal.id"
                      @click.stop="showCommentModal"
                      ><i></i>
                      <span class="comment-count" v-if="goal.commentCount > 0">{{
                        goal.commentCount
                      }}</span>
                    </span>
                    <action-tooltip
                      :target="'comment-badge-' + uuid + goal.id"
                      text="Comment"
                      :actions="['c']"
                    />
                    <span
                      :id="'goal-edit-' + uuid + '-' + goal.id"
                      class="edit-icon"
                      @click.stop="enableTitleInlineEdit"
                      ><i></i
                    ></span>
                    <action-tooltip
                      :target="'goal-edit-' + uuid + '-' + goal.id"
                      text="Edit"
                      :actions="['e']"
                    />
                    <span
                      class="expander-icon"
                      :id="'goal-expander-' + uuid + '-' + goal.id"
                      @click.stop="goToGoalPage"
                      v-if="showDetailsPageNavigator"
                      ><i></i
                    ></span>
                    <action-tooltip
                      :target="'goal-expander-' + uuid + '-' + goal.id"
                      text="Open"
                      :actions="['Shift', rightKey]"
                      v-if="showDetailsPageNavigator"
                    />
                  </span>
                </h2>
                <spinner size="xs" class="inline-edit-loader" v-if="editable && isSubmittingGoal" />
                <div
                  class="text-prime text-medium archive-meta col"
                  v-if="goal.isArchived && showGoalArchivalInfo"
                >
                  Archived on {{ formatDate(goal.archivedAt) }} by
                  {{ userById(goal.archiverId).displayName }}
                </div>
              </div>
              <div class="col-auto row mx-0 align-self-start">
                <div
                  v-if="showType"
                  class="col-auto goal-type text-small-1 d-flex align-items-center"
                >
                  <span
                    class="text-truncate"
                    :id="'team-' + uuid + '-' + goal.id"
                    :title="teamInfo.name"
                    v-no-capture
                    >{{ teamInfo.name }}</span
                  >
                  <ancestors-popover
                    :target="'team-' + uuid + '-' + goal.id"
                    :team-id="goal.teamId"
                  />
                </div>
                <div v-if="showTimeline" class="col-auto text-secondary text-small-1 goal-timeline">
                  {{ getTimelineName(goal.timeline) }}
                </div>
                <div class="goal-status-icon">
                  <review-goal-contributions-popover
                    class="goal-contribution-error"
                    :goal="goal"
                    v-if="Math.round(goal.totalContribution * 100) < 100"
                  />
                  <div class="checkin-overdue" v-else-if="isCheckinOverdue">
                    <checkin-overdue :goal="goal" />
                  </div>
                </div>
                <div class="history-progress-container">
                  <div class="col-auto goal-checkin-history px-0">
                    <div class="d-flex" v-if="viewType === 'list'">
                      <div
                        class="outcome variant outcome-color text-medium-1 bold"
                        :data-variant="colorVariant"
                        data-apply-color
                        v-if="goal.isClosed"
                      >
                        {{ getOutcomeLabel(goal.outcome) }}
                      </div>
                      <progress-bar
                        class="d-none d-lg-block align-self-center"
                        :completed="Math.round(goal.progress * 100)"
                        :variant="colorVariant"
                        width="80px"
                        height="10px"
                        v-else
                      />
                    </div>
                    <checkin-history :goal="goal" :id="goal.id" v-else />
                  </div>
                  <div
                    class="col-auto goal-completion status-color variant"
                    :data-variant="colorVariant"
                    data-apply-color
                  >
                    {{ percentage(goal.progress) }}
                  </div>
                </div>
                <div class="col-auto edit-menu">
                  <goal-options
                    v-if="showOptions"
                    :goal="goal"
                    :source="source"
                    @delete-goal="$emit('delete-goal')"
                    @archive-goal="$emit('archive-goal')"
                    @unarchive-goal="$emit('unarchive-goal')"
                    @move-goal="$emit('move-goal')"
                    @close-goal="$emit('close-goal')"
                    @reopen-goal="$emit('reopen-goal')"
                    @show-award-modal="showAwardModal"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            class="list-view-goal-description text-prime text-medium-1"
            v-html="URLify(goal.description)"
            :title="goal.description"
            v-if="viewType === 'list' && expanded && !!goal.description"
            v-no-capture
          ></div>
          <div class="goal-details" v-if="viewType === 'detail'">
            <progress-bar
              :completed="Math.round(goal.progress * 100)"
              :variant="colorVariant"
              width="100%"
              height="14px"
            />
            <div class="text-normal-1 bold pt-2 mt-1" v-if="goal.isClosed">
              This Goal was
              <span class="outcome variant" :data-variant="colorVariant" data-apply-color>{{
                getOutcomeLabel(goal.outcome)
              }}</span>
            </div>
            <div
              class="pt-3 goal-description text-medium"
              v-html="URLify(goal.description)"
              v-no-capture
            ></div>
          </div>
        </div>
        <b-collapse
          class="expanded-section"
          :id="`goal-${goal.id}`"
          v-model="expandAccordion"
          v-if="renderAccordion"
        >
          <div
            class="krs"
            :class="'krs-' + goal.id"
            v-if="
              krUnderCreation ||
                goal.keyResults.filter(x => goal.isArchived || !x.isArchived).length > 0
            "
            :key="krsContainerKey"
          >
            <div ref="krsContainer">
              <template v-for="kr in goal.keyResults">
                <kr-row
                  :key="kr.id"
                  :kr="kr"
                  :data-id="kr.id"
                  :orgId="goal.organizationId"
                  :goalId="goalId"
                  :type="type"
                  :show-last-checkin="true"
                  :selectable="selectable"
                  :source="source"
                  view-type="list"
                  @kr-unarchived="$emit('kr-unarchived')"
                  @kr-archived="$emit('kr-archived')"
                  @kr-closed="$emit('kr-closed')"
                  @kr-reopened="$emit('kr-reopened')"
                  @open="openKr($event, kr)"
                  @awarded="$emit('kr-awarded')"
                  :level="getNextLevel"
                  v-if="goal.isArchived || !kr.isArchived"
                />
              </template>
              <kr-row
                :kr="krUnderCreation"
                :orgId="goal.organizationId"
                :goalId="goalId"
                :type="type"
                :selectable="selectable"
                :source="source"
                :draggable="false"
                view-type="list"
                @created="onKRCreation"
                @cancel-edit="krUnderCreation = null"
                v-if="krUnderCreation"
              />
            </div>
          </div>
          <template>
            <div class="add-kr-bar" v-if="viewType === 'detail'">
              <b-button
                variant="link"
                class="text-secondary text-medium p-0 border-0"
                :disabled="!!krUnderCreation"
                @click="addKeyResultFromLink"
                >Add Key Result or milestone
              </b-button>
            </div>
            <div class="add-kr-link" v-else-if="!isGoalEmpty">
              <b-button
                variant="link"
                class="text-secondary text-medium p-0 border-0"
                :disabled="!!krUnderCreation"
                @click="addKeyResultFromLink"
                >Add Key Result
              </b-button>
            </div>
          </template>
          <template
            v-if="showChildGoals && childGoals && childGoals.length > 0 && viewType === 'list'"
          >
            <div
              :class="{
                'level-1-child-goals': type === 'goal',
                'child-goals': true,
                ['child-goals-' + goal.id]: true
              }"
              ref="childGoalContainer"
              :key="childGoalsContainerKey"
            >
              <goal-viewer
                v-for="goal in childGoals"
                :key="'child-goal-' + goal.id"
                :goal-id="goal.id"
                type="child_goal"
                :source="source"
                :show-gripper="true"
                :show-type="true"
                :show-child-goals="true"
                :show-timeline="true"
                :data-id="getGoalRelationshipId(goal, goalId)"
                :parent-goal-id="goalId"
                @archive-goal="fetchChildGoals"
                @child-goal-arrange="onChildGoalArrange"
                :level="getNextLevel"
                class="draggable-item"
              />
            </div>
          </template>
          <div
            class="no-kr d-flex align-items-center flex-wrap"
            v-if="isGoalEmpty && !krUnderCreation"
          >
            <span>A Goal without Key Results is like walking without direction.</span>
            <b-button variant="link" class="text-secondary text-medium p-0" @click="addKeyResult"
              >Add Key Results or Milestones
            </b-button>
          </div>
        </b-collapse>
      </div>
    </div>
    <give-award
      :context="awardContext"
      :team-id="goal.teamId"
      v-model="isAwardModalShown"
      @awarded="$emit('goal-awarded')"
      v-if="isAwardModalShown"
    />
  </div>
</template>

<script>
import ActionTooltip from '@/components/ActionTooltip'
import GiveAward from '@/components/award/GiveAward'
import CheckinHistory from '@/components/CheckinHistory'
import CheckinOverdue from '@/components/CheckinOverdue'
import CollapseCaret from '@/components/CollapseCaret'
import AncestorsPopover from '@/components/goal/AncestorsPopover'
import GoalOptions from '@/components/goal/GoalOptions'
import ParentGoalsBar from '@/components/goal/ParentGoalsBar'
import ParentGoalsPopover from '@/components/goal/ParentGoalsPopover'
import ReviewGoalContributionsPopover from '@/components/goal/ReviewGoalContributionsPopover'
import KrRow from '@/components/KrRow'
import ProgressBar from '@/components/ProgressBar'
import Spinner from '@/components/Spinner'
import Tooltip from '@/components/Tooltip'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { VARIANT_MAP } from '@/constants/common'
import events from '@/constants/events'
import { GOAL_ICON_MAP, GOAL_ROW } from '@/constants/goal'
import { KEY_CODES } from '@/constants/keyCodes'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'
import { formatHyphenatedString, newKRData } from '@/goalUtils'
import { defaultInterval, intervalWithId } from '@/interval'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import ExpandCollapseMixin from '@/mixins/ExpandCollapseMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import * as keymapService from '@/services/keymapService/keymapService'
import { formatISOStringDate } from '@/utils/formatDate'
import getEntityAwardContext from '@/utils/getEntityAwardContext'
import getGoalStatus from '@/utils/getGoalStatus'
import getOutcomeLabel from '@/utils/getOutcomeLabel'
import handleClickNavigation from '@/utils/handleClickNavigation'
import multiClickHandler from '@/utils/multiClickHandler'
import { getGoalPageRouteConfig, getKrPageRouteConfig } from '@/utils/navigationConfigs'
import prettifyKey from '@/utils/prettifyKey'
import URLify from '@/utils/URLify'
import { BButton, BCollapse, VBToggle } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { trackEvent } from '../analytics'
import expansionState from '../expansionState'
import OwnershipShort from './OwnershipShort'
import showModal from '@/utils/showModal'
import GoalModal from '@/components/goal/GoalModal'
import { ENTITY_TYPE } from '@/constants/entities'

/**
 * This is the component for Goal. It shows KRs and Child goals inside it
 *
 * @emits:
 * - delete-goal
 * - archive-goal
 * - unarchive-goal
 * - close-goal
 * - reopen-goal
 * - kr-unarchived: when kr is unarchived
 * - kr-archived: when kr is archived
 * - kr-closed: when kr is closed
 * - kr-reopened: when closed kr is reopened
 * - post-checkin: when checkin is posted
 * - kr-awarded: When an award is given on a KR
 * - goal-awarded: When an award is given on the Goal
 */
export default {
  name: GOAL_ROW,
  mixins: [DragAndDropMixin, ExpandCollapseMixin, MediaQueryMixin, ComponentIdMixin, EventBusMixin],
  components: {
    KrRow,
    OwnershipShort,
    CheckinHistory,
    ProgressBar,
    CheckinOverdue,
    ActionTooltip,
    GoalOptions,
    BCollapse,
    ParentGoalsBar,
    BButton,
    GiveAward,
    Tooltip,
    AncestorsPopover,
    ParentGoalsPopover,
    CollapseCaret,
    Spinner,
    ReviewGoalContributionsPopover
  },
  directives: {
    'b-toggle': VBToggle
  },
  props: {
    goalId: Number,
    parentGoalId: Number,
    level: Number,
    goalPreProcessor: Function,
    expand: {
      type: Boolean,
      default: false
    },
    canToggleExpansion: {
      type: Boolean,
      default: true
    },
    source: String,
    showAlignment: {
      type: Boolean,
      default: false
    },
    /**
     *This tells if it needs to show goal type
     */
    showType: {
      type: Boolean,
      default: false
    },
    /**
     *This tells if it needs to show goal timeline
     */
    showTimeline: {
      type: Boolean,
      default: false
    },
    showGripper: {
      type: Boolean,
      default: false
    },
    /*
            this controls visibility of go to Goal page icon
             */
    showDetailsPageNavigator: {
      type: Boolean,
      default: true
    },
    /**
     * Acceptable values: list | detail
     * This is need to style goal-viewer differently for list view and detail view
     */
    viewType: {
      type: String,
      default: 'list'
    },
    /*
          This can be goal, child_goal
           */
    type: {
      type: String,
      default: 'goal'
    },
    /*
          This tells is goal-viewer needs to show child goals. This is used in below scenarios
          * on goals page, user can toggle between nested/flat view
          * on goal page, we don't need to show child goals with goal viewer. It is shown as separate tab at the bottom
           */
    showChildGoals: {
      type: Boolean,
      default: true
    },
    showGoalArchivalInfo: {
      default: true
    },
    /**
     * Render KRs on first load
     */
    renderKrsOnload: {
      default: false
    },
    /**
     * this is the timeline of the context in which goal was fetch.
     * It is required to fetch the child goals for the same timeline
     */
    selectedTimeline: {
      type: Object,
      default: () => defaultInterval()
    },
    /**
     * Whether goal row can be selected or not. If goal is not selectable, implicitly its KRs are also not selectable
     */
    selectable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    const expanded =
      this.canToggleExpansion && expansionState.exists('goal', this.goalId)
        ? !!expansionState.get('goal', this.goalId)
        : this.expand
    return {
      expanded,
      // *expandAccordion* is copy of *expanded* state. For the first time when goal is expanded,
      // we first render the B-Collapse and then expand it
      expandAccordion: expanded,
      krsContainerKey: `krs-${1}`,
      childGoals: null,
      rightKey: prettifyKey('<right>')[0],
      editable: false,
      lastKey: null,
      KEY_CODES,
      goalTitle: null, // contains unsaved title when it is being edited
      showOptions: false,
      // render items that are not supposed to be shown upfront. i.e KRs, comments
      renderAccordion: this.renderKrsOnload || expanded,
      isAwardModalShown: false,
      VARIANT_MAP,
      krUnderCreation: null,
      isSubmittingGoal: false,
      childGoalsContainerKey: `chd-${1}`
    }
  },
  computed: {
    hasParentGoals() {
      return this.goal.parentGoalIds?.length > 0
    },
    awardContext() {
      return {
        ...getEntityAwardContext(this.goal, 'goal'),
        goalId: this.goal.id
      }
    },
    userId() {
      return this.$store.getters.user.id
    },
    goal() {
      const goal = this.$store.getters.goalById(this.goalId)
      return this.goalPreProcessor ? this.goalPreProcessor(goal) : goal
    },
    org() {
      if (this.goal) {
        return this.$store.getters.orgById(this.goal.organizationId)
      } else {
        return null
      }
    },
    teamInfo() {
      if (this.goal) {
        return this.$store.getters.teamById(this.goal.teamId)
      } else {
        return null
      }
    },
    isCheckinOverdue() {
      return (
        this.goal &&
        !this.goal.isClosed &&
        this.goal.keyResults.filter(kr => !kr.isArchived).some(R.propEq('isCheckinOverdue', true))
      )
    },
    isGoalEmpty() {
      if (this.showChildGoals) {
        return (
          this.childGoals &&
          this.childGoals.length === 0 &&
          this.goal.keyResults.filter(kr => this.goal.isArchived || !kr.isArchived).length === 0
        )
      } else {
        return (
          this.goal.keyResults.filter(kr => this.goal.isArchived || !kr.isArchived).length === 0
        )
      }
    },
    goalIcon() {
      return GOAL_ICON_MAP[this.colorVariant]
    },
    goalStatusLabel() {
      return formatHyphenatedString(this.goalStatus())
    },
    colorVariant() {
      return (
        VARIANT_MAP['outcome-' + this.goal.outcome] || VARIANT_MAP['status-' + this.goalStatus()]
      )
    },
    getGripperClass() {
      if (this.type == 'child_goal') {
        return 'child-goal-gripper child-goal-gripper-' + this.parentGoalId
      } else if (this.type == 'goal') {
        return 'goal-gripper'
      }
      return null
    },
    getGripperStyle() {
      if (this.level > 0) {
        return {
          left: '13px',
          top: '10px'
        }
      }
      return {}
    },
    getNextLevel() {
      return this.level + 1
    }
  },
  methods: {
    ...mapActions(['changeRank', 'getGoals', 'getGoal', 'patchGoal']),
    percentage(n) {
      return `${Math.round(n * 100)}%`
    },
    goalStatus() {
      return getGoalStatus(this.goal)
    },
    goToGoalPage(event = {}, krId) {
      const location = getGoalPageRouteConfig({
        orgId: this.goal.organizationId,
        goalId: this.goalId,
        query: {
          krId
        }
      })
      handleClickNavigation.call(this, event, location)
    },
    URLify,
    goToKrPage(event = {}, kr) {
      const location = getKrPageRouteConfig({
        krId: kr.id,
        orgId: this.goal.organizationId
      })
      handleClickNavigation.call(this, event, location)
    },
    enableKRDragAndDrop() {
      this.enableDragAndDrop({
        containers: [this.$refs.krsContainer],
        onDrop: this.onDrop,
        handleName: 'kr-gripper',
        context: 'krs'
      })
    },
    enableChildGoalDragAndDrop() {
      this.enableDragAndDrop({
        containers: [this.$refs.childGoalContainer],
        onDrop: this.onChildGoalDrop,
        handleName: 'child-goal-gripper-' + this.goalId,
        context: 'childGoals'
      })
    },
    onChildGoalArrange({ subjectId, beforeId }) {
      this.childGoals = this.shiftItem(this.childGoals, subjectId, beforeId)
    },
    getGoalRelationshipId(goal, parentId) {
      const parentRelationships = goal.parentRelationships
      const parentRelationship = parentRelationships?.find(x => x.parentId === parentId)
      return parentRelationship?.id
    },
    async onDrop({ afterId, beforeId, subjectId }) {
      const keyResults = this.goal.keyResults
      try {
        await this.changeRank({
          orgId: R.path(['org', 'id'], this),
          type: 'key_result',
          subjectId,
          beforeId,
          afterId
        })
        const newKRs = this.shiftItem(keyResults, subjectId, beforeId)
        const goal = R.assoc('keyResults', newKRs, this.goal)
        this.$store.commit('SET_GOAL', goal)
        trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.REARRANGE, EVENT_NAME.KR)
      } catch (e) {
        // Revert the drag. Re-rendering the list
        this.setKrsContainerKey()
        this.$nextTick(function() {
          this.enableKRDragAndDrop()
        })
      }
    },
    async onChildGoalDrop({ afterId, beforeId, subjectId }) {
      try {
        await this.changeRank({
          orgId: R.path(['org', 'id'], this),
          type: 'child_goals',
          subjectId,
          beforeId,
          afterId
        })
        this.childGoals = this.shiftItem(this.childGoals, subjectId, beforeId)
        trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.REARRANGE, EVENT_NAME.CHILD_GOAL)
      } catch (e) {
        // Revert the drag. Re-rendering the list
        this.setChildGoalsContainerKey()
        this.$nextTick(function() {
          this.enableChildGoalDragAndDrop()
        })
      }
    },
    setChildGoalsContainerKey() {
      // do error handling
      let counter = this.childGoalsContainerKey.split('-')[1]
      this.childchildGoalsContainerKey = `chd-${counter++}`
    },
    setKrsContainerKey() {
      // do error handling
      let counter = this.childGoalsContainerKey.split('-')[1]
      this.krsContainerKeyContainerKey = `krs-${counter++}`
    },
    openKr(event, kr) {
      this.goToKrPage(event, kr)
    },
    clearSelection() {
      const selectedItem = keymapService.selectedListItem(document)
      if (selectedItem) selectedItem.classList.remove('selected')
    },
    selectCurrentGoal() {
      this.$el.classList.add('selected')
    },
    updateInitiativesExpansion() {
      this.$el.querySelectorAll('.kr').forEach(kr => {
        const krVue = kr.__vue__
        if (this.expanded) {
          // add "select-list-item" class to initiatives of expanded KRs only
          if (
            krVue.expanded &&
            kr.classList.contains('select-list-item') &&
            krVue.$refs.initiatives
          ) {
            this.updateExpansionMiddleware(
              krVue.expanded,
              '.initiative-row',
              krVue.$refs.initiatives
            )
            if (krVue.showClosed) {
              this.updateExpansionMiddleware(
                krVue.expanded,
                '.initiative-row',
                krVue.$refs.closedInitiatives.$el
              )
            }
          }
        } else {
          this.updateExpansionMiddleware(this.expanded, '.initiative-row', kr)
        }
      })
    },
    updateKRsExpansion() {
      const goals = [this.$el, ...this.$el.querySelectorAll('.goal-container')]
      goals.forEach(goal => {
        const component = goal.__vue__
        if (component.expanded && goal.classList.contains('select-list-item')) {
          // add "select-list-item" class to krs of expanded goals only
          this.updateExpansionMiddleware(
            component.expanded,
            '.kr',
            goal.querySelector('.krs-' + component.goalId)
          )
          this.updateExpansionMiddleware(
            component.expanded,
            '.child-goals-' + component.goalId + '> .goal-container',
            goal.querySelector('.child-goals-' + component.goalId)
          )
        } else {
          this.updateExpansionMiddleware(component.expanded, '.kr', goal)
          this.updateExpansionMiddleware(component.expanded, '.goal-container', goal)
        }
      })
    },
    handleExpanderClick() {
      this.toggleExpansionAndSelect()
    },
    handleTitleClick() {
      multiClickHandler({
        onSingleClick: () => {
          const selectedText = window.getSelection().toString()
          if (!selectedText) {
            this.isDesktopMode ? this.toggleExpansionAndSelect() : this.goToGoalPage()
          }
        },
        onDoubleClick: () => {
          this.editable = true
          this.goalTitle = this.goal.title
        }
      })
    },
    toggleExpansionAndSelect() {
      if (this.canToggleExpansion) {
        this.expanded = !this.expanded
      }
      if (this.selectable) {
        this.$nextTick(function() {
          this.clearSelection()
          this.selectCurrentGoal()
        })
      }
    },
    async fetchChildGoals() {
      this.childGoals = await this.getGoals({
        orgId: this.goal.organizationId,
        options: {
          parentId: this.goal.id,
          isArchived: this.goal.isArchived,
          expand: 'parent_goal_ids,parent_relationships',
          orderBy: 'rank_in_parent~id'
        }
      })
    },
    updateExpansionMiddleware(value, itemSelector, container) {
      if (this.selectable) {
        this.updateExpansion(value, itemSelector, container)
      }
    },
    handleExpansionState() {
      this.updateKRsExpansion()
      this.updateInitiativesExpansion()
    },
    async saveGoalTitle() {
      try {
        this.isSubmittingGoal = true
        await this.patchGoal({
          id: this.goal.id,
          organizationId: this.goal.organizationId,
          title: this.goalTitle
        })
        this.editable = false
        EventBus.$emit('RELOAD_ACTIVITY_EVENTS', this.goal.id)
      } catch (e) {
        let message =
          R.path(['response', 'data', 'detail', 'title', '0'], e) ||
          R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      } finally {
        this.isSubmittingGoal = false
      }
    },
    enableTitleInlineEdit() {
      this.editable = true
      this.goalTitle = this.goal.title
    },
    cancelInlineEditing() {
      this.lastKey = KEY_CODES.ESCAPE
      this.editable = false
      this.goalTitle = this.goal.title
    },
    formatDate: formatISOStringDate,
    userById(id) {
      return this.$store.getters.userById(id) || {}
    },
    getTimelineName(id) {
      const interval = intervalWithId(id)
      return interval && (interval.shortName || interval.name)
    },
    addKeyResultFromLink() {
      this.addKeyResult()
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.ADD_KR, EVENT_NAME.INLINE_LINK)
    },
    addKeyResult() {
      this.krUnderCreation = newKRData(this.userId)
    },
    showAwardModal() {
      this.isAwardModalShown = true
    },
    getOutcomeLabel,
    onParentGoalAlignmentChange({ parentIds, childId }) {
      if (this.showChildGoals) {
        const isChildGoalRemoved =
          this.childGoals?.some(goal => goal.id === childId) && !parentIds.includes(this.goalId)
        const isChildGoalAdded =
          !this.childGoals?.some(goal => goal.id === childId) && parentIds.includes(this.goalId)
        if (this.showChildGoals && (isChildGoalAdded || isChildGoalRemoved)) {
          if (this.expanded) {
            this.fetchChildGoals()
          } else {
            this.childGoals = null // child goals will be fetched when goal is expanded
          }
        }
      }
    },
    onChildGoalAddition({ parentIds }) {
      if (this.showChildGoals && parentIds.includes(this.goalId)) {
        this.fetchChildGoals()
        this.expanded = true
      }
    },
    selectLastKR() {
      const krs = document.querySelectorAll('.krs .select-list-item')
      R.last(krs).classList.add('selected')
    },
    onKRCreation() {
      this.krUnderCreation = null
      this.updateKRsExpansion()
      // selecting last kr because newly created KR would be last in the list
      this.$nextTick(this.selectLastKR)
    },
    showCommentModal() {
      showModal({
        component: GoalModal,
        id: `${ENTITY_TYPE.GOAL}-${this.goal.id}`,
        props: {
          show: true,
          goal: this.goal
        }
      })
    }
  },
  async mounted() {
    if (this.expanded) {
      this.$el.classList.add('expanded')
    }
    if (this.goal && this.expanded) {
      if (this.showChildGoals) {
        await this.fetchChildGoals()
      }
      this.$nextTick(function() {
        this.enableKRDragAndDrop()
        this.enableChildGoalDragAndDrop()
      })
    }
    this.handleExpansionState()
    this.listenToEventBus(events.PARENT_GOAL_ALIGNMENT_CHANGED, this.onParentGoalAlignmentChange)
    this.listenToEventBus(events.CHILD_GOAL_CREATED, this.onChildGoalAddition)
  },
  watch: {
    async expanded(value) {
      if (!this.childGoals && this.showChildGoals) {
        this.fetchChildGoals()
      }
      if (!this.renderAccordion) {
        this.$nextTick(() => {
          setTimeout(() => (this.expandAccordion = value), 0)
        })
        this.renderAccordion = true
      } else {
        this.expandAccordion = value
      }
    },
    childGoals(newValue, oldValue) {
      if (!oldValue && newValue) {
        this.$nextTick(() => {
          this.enableChildGoalDragAndDrop()
        })
      }
    },
    expandAccordion(value) {
      expansionState.set('goal', this.goalId, value)
      this.handleExpansionState()
      // while expanding make only all the goals
      this.updateExpansionMiddleware(
        value,
        value ? '.level-1-child-goals > .goal-container' : '.goal-container'
      )
      if (value) {
        this.$nextTick(function() {
          this.enableKRDragAndDrop()
          this.childGoals && this.enableChildGoalDragAndDrop()
        })
      }
    },
    showChildGoals(showChildGoals) {
      if (showChildGoals && !this.childGoals) {
        this.fetchChildGoals()
      }
    },
    editable() {
      if (this.editable) {
        this.selectable &&
          this.$nextTick(function() {
            this.clearSelection()
            this.selectCurrentGoal()
          })
        this.goalTitle = this.goal.title
        this.lastKey = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/goals';
@import '~@/styles/drag';
@import '~@/styles/components/badge';
@import '~@/styles/components/caret';
@import '~@/styles/components/input';

$grid-gutter-width: 6px;
$corner-radius: 10px;

.row {
  margin-left: -$grid-gutter-width;
  margin-right: -$grid-gutter-width;
}

.col,
.col-auto {
  padding-left: $grid-gutter-width;
  padding-right: $grid-gutter-width;
}

.goal-container .goal-viewer-wrapper .goal-content {
  border-top: 1px solid $borderSecondary;
}

.goal-container:first-child .goal-viewer-wrapper .goal-content {
  border-top: 1px solid transparent;
}

.goal-container.selected > .goal-viewer-wrapper,
> .goal-viewer-wrapper.expanded {
  box-shadow: 0 1px 1px rgba(168, 168, 168, 0.25);
}

.goal-container {
  &.selected,
  &.self-or-child-selected,
  .goal-viewer-wrapper.expanded {
    .goal-content,
    + .goal-container .goal-content {
      border-top-color: transparent;
    }
  }
}
.show-gripper {
  padding-left: 10px;
}

.goal-container:not(.type-child_goal) > .goal-viewer-wrapper.expanded {
  margin-bottom: 24px;
}

.goal-viewer-wrapper.list.expanded {
  border: 1px solid $borderPrimary;

  > .goal-content-container > .top-bar {
    padding: 3px 0;
  }
}

.goal-viewer-wrapper.detail {
  margin-bottom: 10px;
}

.goal-container.detail {
  $titleSize: 21px;

  &.expanded {
    border: none;
    margin-left: 0;
    margin-right: 0;

    .goal-viewer-wrapper,
    &.self-or-child-selected .goal-viewer-wrapper {
      @extend .card-shadow-primary;
      background-color: #fff;
    }
  }

  .goal-title {
    font-size: $titleSize;
    font-weight: 600;
    padding-top: 4px;
  }

  .goal-content-container {
    padding: 25px;
    margin: 0;
  }

  .expanded .goal-title-container {
    padding: 0 30px 0 36px;
  }

  .goal-completion {
    font-size: $titleSize;
  }

  .goal-title-container,
  .kr {
    margin-left: 0;
    margin-right: 0;
  }

  .krs {
    padding-left: 0px;
  }

  .goal-content {
    padding: 10px 0;
  }
}

.goal-viewer-wrapper {
  min-width: 0;
  border-radius: $corner-radius;
  margin-bottom: 0;
  transition: margin-bottom 100ms ease-in;
  background-color: $documentColor;
  /* On expansion, we are adding the margin at the bottom. */
}

.goal-container.expanded.self-or-child-selected .goal-viewer-wrapper {
  background-color: unset;
}

.goal-viewer-wrapper.detail > .goal-content-container .top-bar {
  margin-bottom: 24px;
  padding-bottom: 36px;
}

.goal-viewer-wrapper.detail .kr {
  padding-left: 17px;
  padding-right: 33px;
}

.top-bar {
  border-top-left-radius: $corner-radius;
  border-top-right-radius: $corner-radius;
  padding-top: 0;
  transition: padding ease-in 100ms;
}

.goal-viewer-wrapper:not(.expanded) .top-bar {
  border-bottom-left-radius: $corner-radius;
  border-bottom-right-radius: $corner-radius;
  border: 1px solid transparent;
}

.goal-container.selected > .goal-viewer-wrapper > .goal-content-container > .top-bar {
  background-color: $selectionColor !important;
}

.icons-col {
  padding-right: 3px;
  padding-left: 0px;
}

.goal-title-container {
  margin-left: 0;
  margin-right: 0;
  position: relative;
  padding-left: 16px;
  padding-right: 22px;
  border-radius: $corner-radius;
}

.goal-content {
  padding: 12px 0;
}

.type-child_goal .goal-content {
  padding: 6px 0;
}

.type-child_goal .collapse-caret {
  margin-top: 13px;
}

.collapse-caret {
  margin-right: 6px;
}

.goal-container:hover {
  > .gripper {
    display: initial;
  }
}

.goal-title-col {
  padding-left: 0;
  padding-right: 0;
  cursor: pointer;
}

.goal-title-input {
  padding: 6px 3px 6px 3px;
  font-weight: 500;
  color: $primary;
  width: 100%;
  margin-top: -2px;
}

.goal-viewer-wrapper.detail .goal-title-input {
  font-size: 21px;
  padding: 6px 3px;
  margin-top: -3px;
  font-weight: 600;
}

.inline-edit {
  padding-right: 6px;
}

.inline-edit-loader {
  position: absolute;
  right: 14px;
  top: 8px;
}

.detail .inline-edit-loader {
  right: 16px;
  top: 10px;
}

.goal-title {
  font-size: 0.9375rem;
  padding-top: 5px;
  padding-left: 3px;
  line-height: 1.4;
  margin-bottom: 7px;
}

.goal-title-value {
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.icons-list {
  height: 20px;
  min-width: 75px;
  margin-top: 2px;
  display: flex;
  align-items: center;
}

.icons-list > :not(:first-child) {
  margin-left: 2px;
}

.collapse-caret {
  margin-top: 16px;
}

.top-bar /deep/ .edit-menu-button[aria-expanded='false'] {
  display: none;
}

.top-bar:hover /deep/ .edit-menu-button {
  display: block;
}

.goal-description {
  white-space: pre-wrap;
  word-break: break-word;
}

.goal-type,
.goal-timeline {
  padding-right: 8px;
  color: $secondary;
  margin-top: 9px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.goal-timeline {
  width: 75px;
}

.goal-type {
  max-width: 120px;
  margin-top: 6px;
}

.goal-completion {
  font-size: 0.9375rem;
  padding-top: 6px;
  padding-right: 0;
  padding-left: 10px;
  width: 52px;
  text-align: center;
  font-weight: bold;
}

.detail .goal-completion {
  width: 60px;
  padding-top: 2px;
}

.goal-checkin-history {
  display: flex;
  align-self: start;
  padding-right: 0;
}

.detail .goal-checkin-history {
  margin-top: 15px;
}

.list .goal-checkin-history {
  margin-top: 9px;
}

.goal-content.expanded {
  border-top: 0;
}

.gripper {
  position: absolute;
  left: -13px;
  top: 15px;
  display: none;
  cursor: move;
}

.add-kr-bar {
  padding-left: 140px;
  padding-top: 8px;
  padding-bottom: 50px;
  transition: opacity ease-in 150ms;
}

.add-kr-link {
  padding-left: 108px;
  opacity: 0;
  transition: opacity ease-in 200ms;
  padding-bottom: 10px;
}

.expanded-section:hover > .add-kr-link {
  opacity: 1;
}

.no-kr {
  padding-left: 80px;
  padding-bottom: 20px;
  padding-top: 20px;
  font-size: 0.875rem;

  a {
    color: $secondary;
  }
}

.detail .no-kr {
  padding-bottom: 44px;
}

.goal-icon-element {
  margin-right: 6px;
}

.goal-container:hover,
.expanded,
.selected {
  > .goal-viewer-wrapper > .goal-content-container > .top-bar .collapse-caret {
    opacity: 1;
  }
}

/deep/ .kr-wrapper {
  padding-left: 28px;
}

.history-progress-container {
  display: flex;
  flex-direction: row;
}

.comment-count {
  margin-left: 2px;
}

.comment-badge-icon[data-comment-count='0'] {
  opacity: 0;
}

.expander-icon,
.edit-icon {
  opacity: 0;
}

.expander-icon,
.edit-icon {
  transition: opacity ease-out 200ms;
}

.goal-title:hover {
  .comment-badge-icon,
  .expander-icon,
  .edit-icon {
    opacity: 1;
  }
}

.edit-menu {
  padding-top: 5px;
  width: 36px;
  flex-shrink: 0;
}

.outcome {
  margin-top: -3px;
}

.goal-details {
  padding-right: 72px;
  padding-left: 110px;
}

.goal-icon-greyed {
  @include dimension(20px, 20px);
  background-size: contain;
  margin: 2px 8px 2px 3px;
}

.list-view-goal-description {
  @include lineClamp(2);
  padding-left: 112px;
  padding-right: 60px;
  margin-bottom: 15px;
}

@include media-breakpoint-down(md) {
  .goal-details {
    padding-right: 30px;
    padding-left: 30px;
  }
  .goal-container.list:not(.type-child_goal) {
    padding: 0;
  }
  .expander-icon {
    display: inherit;
  }
  .collapse-caret,
  .goal-icon-container,
  .goal-type,
  .goal-timeline {
    display: none;
  }
  .section-title {
    padding-left: 10px;
  }
  .goal-completion {
    margin-right: 0;
    text-align: center;
  }
  .goal-viewer-wrapper.detail {
    .goal-title-col {
      align-self: baseline;
    }

    .goal-checkin-history {
      padding: 2px 0;
    }
  }
  .goal-container.selected {
    .goal-title-container {
      background-color: inherit;
    }
  }
  .goal-title-container {
    padding: 0;
  }

  /deep/ .edit-menu,
  .goal-gripper {
    display: none !important;
  }
  .child-goal-gripper {
    display: none !important;
  }
  .krs /deep/ .kr:last-child {
    .kr-checkin-measurement-container {
      border-bottom: none;
    }
  }
  /deep/ .goal-viewer-wrapper.detail .kr {
    padding: 0;
  }
  .no-kr {
    padding-top: 0;
    padding-left: 0;
  }
  .goal-progress-bar {
    padding: 0;
  }
  .top-bar {
    padding: 0 10px;
  }
  .goal-viewer-wrapper.expanded .top-bar {
    padding: 0 10px 20px;
  }
  .icons-list,
  .goal-status-icon {
    display: none;
  }
  .add-kr-bar {
    padding-left: 40px;
  }
  .list-view-goal-description {
    padding-left: 70px;
  }
}

@include media-breakpoint-up(md) {
  .goal-checkin-history {
    height: 20px;
  }
}

.checkin-overdue {
  margin-top: 12px;
}

.goal-contribution-error {
  margin-top: 12px;
  margin-right: 8px;
}

.detail .goal-contribution-error {
  margin-top: 13px;
}

.goal-status-icon {
  width: 20px;
}

.level-1-child-goals {
  padding-right: 20px;
}

.child-goals {
  margin-bottom: 24px;
}

.type-child_goal {
  padding-left: 33px;

  .goal-viewer-wrapper,
  .top-bar {
    border-radius: $corner-radius;
  }

  .goal-title-container {
    padding-right: 0;
  }

  .top-bar {
    padding: 0;
  }

  .goal-viewer-wrapper {
    border-color: transparent;

    &,
    &.expanded {
      box-shadow: none;
      border: none;
    }
  }
}

/deep/ .top-bar:hover .choose-parent {
  opacity: 1;
}
</style>

<style lang="scss">
.krs.drag-active {
  .checkin-btn-wrapper {
    display: none !important;
  }

  .kr:hover .gripper {
    display: none;
  }
}

.collapsing {
  overflow: hidden;
}
</style>
